/* TuffBlock AU deck block calculator v2 — behaviour for the #tb-calc markup
   rendered by sections/product-layout-calculator-tuffblock.liquid.

   Architecture (responsive controls, results panel, proportional diagram,
   intro modal, sticky strips/bar, add-to-cart, loadPreset() preset API)
   ported from the final US sandbox's assets/tuffblock-calculator.js.

   Calculation engine is AU's own — ported verbatim from the approved AU
   Claude Design .dc.html files (TuffBlock Calculator AU Desktop v2 /
   Mobile v2, supplied 2026-10-02), NOT from the US IRC-based engine:

   - 4 species: Treated Pine F7, Cypress F5, Hardwood F14, MGP10.
   - Framing-clearance tier picks the source table: "low" (under 400mm) =
     Timber Queensland TDS13 (+ the Hyne T3 Green+ MGP10 standing figure,
     which has no TDS13 entry of its own); "high" (400mm or above) =
     TDS4 Table 4 (all three graded species, not just treated pine) + the
     same Hyne MGP10 table.
   - Support-row solver: 2 support rows use the timber's single-span
     figure; 3+ use its continuous-span figure. Every interval is also
     capped at TuffBlock's own 1500mm maximum spacing, whichever is
     smaller — this cap dominates every Treated Pine size in the "high"
     tier (every published span there already exceeds 1500mm), so all
     of that tier's sizes return identical totals for that species; this
     is correct, not a bug.
   - Overhang: NOT the US's IRC backspan tables. AU's own rule — the
     smallest of BuildTuff's 300mm maximum, 25% of the timber's single-
     span figure, or 50% of the actual adjacent backspan (which itself
     requires at least 2 support rows beyond the two end rows to exist).
   - Joist centres across the deck width are fixed at 450mm — every
     source table assumes this, so there is no separate centres control.
   - Finished deck height: collected nowhere in the UI (the framing-
     clearance tier already encodes the height distinction) — only shown
     as fixed 915mm/150mm guidance text, consistent with the approved
     design's own dormant (never-rendered) finishedHeight field.

   Responsive note: species/joist-size use a native <select> at every
   width (see the section file's own header comment for why), not the US
   v2's segmented-buttons-on-desktop/select-on-mobile dual markup. Board
   direction and framing-clearance keep segmented buttons since those are
   always exactly 2 options. */
(function () {
  var root = document.getElementById('tb-calc');
  if (!root) return;

  // ---------- Reference data ----------

  var AU = {
    species: [
      ['tp', 'Treated Pine F7'],
      ['cy', 'Cypress F5'],
      ['hw', 'Hardwood F14'],
      ['mgp', 'MGP10 (Hyne T3 Green+)']
    ],
    tiers: [
      ['low', 'Under 400mm'],
      ['high', '400mm or above']
    ],
    // size -> [singleSpan, continuousSpan] in mm.
    low: {
      tp: { '90x45': [1200, 1200], '120x45': [1800, 1800] },
      cy: { '100x50': [1500, 1500], '125x50': [1800, 1800] },
      hw: { '75x50': [1200, 1200], '100x50': [1800, 1800] },
      mgp: {
        '90x35': [900, 1500], '140x35': [2300, 2800], '190x35': [3300, 3700],
        '90x45': [1400, 1800], '140x45': [2600, 3000], '190x45': [3600, 4100], '240x45': [4800, 5100]
      }
    },
    high: {
      tp: { '90x45': [1300, 1700], '120x45': [2200, 2200], '140x45': [2600, 2600], '190x45': [3600, 3600], '240x45': [4500, 4500] },
      cy: { '100x50': [1400, 1700], '125x50': [2200, 2200], '150x50': [2600, 2600], '175x50': [3000, 3000], '200x50': [3500, 3500] },
      hw: { '100x50': [2200, 2500], '125x50': [2800, 3200], '150x50': [3400, 3800], '175x50': [3900, 4500], '200x50': [4400, 5100] },
      mgp: {
        '90x35': [900, 1500], '140x35': [2300, 2800], '190x35': [3300, 3700],
        '90x45': [1400, 1800], '140x45': [2600, 3000], '190x45': [3600, 4100], '240x45': [4800, 5100]
      }
    },
    spacing: 450,
    maxBlockSpacing: 1500,
    maxOverhang: 300,
    min: 0.6,
    max: 60,
    defaults: { length: '3.6', width: '3', board: 'width', species: 'tp', tier: 'low', size: '120x45', overhang: false, overhangMm: '300' }
  };

  var packUnits = parseInt(root.getAttribute('data-pack-units'), 10) || 12;
  var packVariantId = root.getAttribute('data-pack-variant-id') || '';
  var cartEnabled = root.getAttribute('data-cart-enabled') === 'true';
  var stripRequiresInteraction = root.getAttribute('data-strip-requires-interaction') === 'true';

  function fmtM(mm) {
    var m = mm / 1000;
    var decimals = (m % 1 === 0) ? 1 : (mm % 100 === 0 ? 1 : 2);
    return m.toFixed(decimals) + 'm';
  }
  function fmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function setWarningVisible(card, show) {
    if (!card) return;
    card.hidden = !show;
    if (!show) {
      card.classList.remove('is-open');
      var toggleBtn = card.querySelector('.tb-warning__toggle');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    }
  }

  function bestPacks(n) {
    if (packUnits <= 0) return { units: 0, text: '—' };
    var q = Math.ceil(n / packUnits);
    return { units: q, text: q + ' × ' + packUnits + '-Pack' };
  }

  /* AU support-row solver — verbatim from the approved .dc.html reference.
     Span rule: 2 support rows -> single-span; 3+ -> continuous-span.
     TuffBlock planning intervals also capped at 1500mm. Overhang is the
     minimum of 300mm, 25% of single-span, or 50% of actual backspan. */
  function solve(runLength, singleSpan, continuousSpan, requestedOverhang) {
    var c = requestedOverhang || 0;
    if (c < 0 || c > AU.maxOverhang || c * 2 >= runLength) return null;
    for (var k = 2; k < 1000; k++) {
      var timberSpan = (k === 2) ? singleSpan : continuousSpan;
      var maxSpan = Math.min(timberSpan, AU.maxBlockSpacing);
      var actualBackspan = (runLength - 2 * c) / (k - 1);
      if (actualBackspan <= maxSpan + 1e-9) {
        var maxOverhang = Math.floor(Math.min(AU.maxOverhang, singleSpan * 0.25, actualBackspan * 0.50));
        if (c > 0 && c > maxOverhang + 1e-9) continue;
        return { k: k, c: c, s: actualBackspan, max: maxSpan, maxOverhang: maxOverhang };
      }
    }
    return null;
  }

  // ---------- Elements ----------

  var lengthInput = root.querySelector('#tb-length');
  var lengthSlider = root.querySelector('#tb-length-slider');
  var lengthError = root.querySelector('#tb-length-error');
  var widthInput = root.querySelector('#tb-width');
  var widthSlider = root.querySelector('#tb-width-slider');
  var widthError = root.querySelector('#tb-width-error');
  var boardButtons = root.querySelectorAll('#tb-board-seg button');
  var orientationHelp = root.querySelector('#tb-orientation-help');
  var speciesSeg = root.querySelector('#tb-species-seg');
  var speciesSelect = root.querySelector('#tb-species-select');
  var speciesTipBtn = root.querySelector('#tb-species-tip-btn');
  var speciesTip = root.querySelector('#tb-species-tip');
  var tierButtons = root.querySelectorAll('#tb-tier-seg button');
  var tierHelp = root.querySelector('#tb-tier-help');
  var sizeSeg = root.querySelector('#tb-joist-seg');
  var sizeSelect = root.querySelector('#tb-joist-select');
  var sizeActual = root.querySelector('#tb-size-actual');
  var notCoveredEl = root.querySelector('#tb-not-covered');
  var warnDeep = root.querySelector('#tb-warning-deep');
  var warnNarrow = root.querySelector('#tb-warning-narrow');
  var warnWidth = root.querySelector('#tb-warning-width');
  var warnCypress = root.querySelector('#tb-warning-cypress');
  var warnTreatment = root.querySelector('#tb-warning-treatment');
  var overhangToggle = root.querySelector('#tb-overhang-toggle');
  var overhangFields = root.querySelector('#tb-overhang-fields');
  var overhangMmInput = root.querySelector('#tb-overhang-mm');
  var overhangHelper = root.querySelector('#tb-overhang-helper');
  var overhangDisabledText = root.querySelector('#tb-overhang-disabled-text');
  var placeTipBtn = root.querySelector('#tb-place-tip-btn');
  var placeTip = root.querySelector('#tb-place-tip');
  var srcToggle = root.querySelector('#tb-src-toggle');
  var srcBody = root.querySelector('#tb-src-body');
  var srcText = root.querySelector('#tb-src-text');
  var resetBtn = root.querySelector('#tb-reset');

  var outTotal = root.querySelector('#tb-out-total');
  var outPerRun = root.querySelector('#tb-out-perrun');
  var outJoists = root.querySelector('#tb-out-joists');
  var outSpacing = root.querySelector('#tb-out-spacing');
  var outSupportSpacing = root.querySelector('#tb-out-support-spacing');
  var outRecommend = root.querySelector('#tb-out-recommend');
  var diagramOkEl = root.querySelector('#tb-diagram-ok');
  var blockedTextEl = root.querySelector('#tb-blocked-text');
  var legendEl = root.querySelector('#tb-legend');
  var diagramLengthEl = root.querySelector('#tb-diagram-length');
  var diagramWidthEl = root.querySelector('#tb-diagram-width');
  var layoutNoteEl = root.querySelector('#tb-layout-note');
  var diagramGrid = root.querySelector('#tb-diagram');
  var diagramFrame = root.querySelector('.tb-diagram-frame');
  var selectionsRows = root.querySelector('#tb-selections-rows');
  var selectionsToggle = root.querySelector('#tb-selections-toggle');
  var selectionsToggleLabel = root.querySelector('#tb-selections-toggle-label');
  var cutlistRows = root.querySelector('#tb-cutlist-rows');
  var disclaimerToggle = root.querySelector('#tb-disclaimer-toggle');
  var disclaimerMore = root.querySelector('#tb-disclaimer-more');

  var introModal = root.querySelector('#tb-intro-modal');
  // document-scoped, not root-scoped: the desktop header card's "Read more"
  // trigger lives in .tb-header, a sibling of #tb-calc rendered before
  // .pl-calculator__box — outside root's own subtree — so a root-scoped
  // query silently misses it (only the mobile pill, which IS inside root,
  // would have worked).
  var introOpenBtns = document.querySelectorAll('[data-tb-intro-open]');
  var introCloseBtns = document.querySelectorAll('[data-tb-intro-close]');
  var introSeen = false;

  var stickyBar = root.querySelector('#tb-sticky-bar');
  var stickyTotal = root.querySelector('#tb-sticky-total');
  var stickyPack = root.querySelector('#tb-sticky-pack');
  var resultsPanel = root.querySelector('.tb-results');
  var seeResultsBtn = root.querySelector('#tb-see-results');
  var mobileMq = window.matchMedia('(max-width: 600px)');

  var desktopStripBottom = document.getElementById('tb-desktop-strip-bottom');
  var desktopStripTop = document.getElementById('tb-desktop-strip-top');
  var desktopStripSection = root.closest('.pl-calculator') || root.closest('.pl-section');

  var selectedTier = AU.defaults.tier;
  var selectedSpecies = AU.defaults.species;
  var selectedSize = AU.defaults.size;
  var selectedBoard = AU.defaults.board;
  var hasEngagedStrip = !stripRequiresInteraction;
  var lastStickyTotal = null, lastStickyPackText = '', lastCombo = null;
  var stickyDismissed = false;
  try { stickyDismissed = sessionStorage.getItem('tb-au-sticky-dismissed') === '1'; } catch (e) {}

  // ---------- Populate species / size controls ----------

  function currentTierTable() { return AU[selectedTier]; }
  function currentSpeciesTable() { return currentTierTable()[selectedSpecies]; }

  // Both representations (desktop segmented buttons, mobile <select>) are
  // rebuilt from the same selectedSpecies/selectedSize/selectedTier state
  // every time — one source of truth, so picking either UI stays in sync
  // with the other automatically, same pattern as board/tier already use.
  function populateSpecies() {
    if (speciesSeg) {
      speciesSeg.innerHTML = AU.species.map(function (s) {
        return '<button type="button" data-species="' + s[0] + '"' + (s[0] === selectedSpecies ? ' class="active"' : '') + '>' + s[1] + '</button>';
      }).join('');
    }
    if (speciesSelect) {
      speciesSelect.innerHTML = AU.species.map(function (s) {
        return '<option value="' + s[0] + '"' + (s[0] === selectedSpecies ? ' selected' : '') + '>' + s[1] + '</option>';
      }).join('');
    }
  }

  function populateSizes() {
    var table = currentSpeciesTable();
    var sizes = table ? Object.keys(table) : [];
    if (sizes.length && sizes.indexOf(selectedSize) === -1) {
      selectedSize = (selectedSpecies === 'mgp' && table['140x45']) ? '140x45' : sizes[Math.min(1, sizes.length - 1)];
    }
    if (sizeSeg) {
      sizeSeg.innerHTML = sizes.map(function (s) {
        return '<button type="button" data-size="' + s + '"' + (s === selectedSize ? ' class="active"' : '') + '>' + s + '</button>';
      }).join('');
    }
    if (sizeSelect) {
      sizeSelect.innerHTML = sizes.map(function (s) {
        return '<option value="' + s + '"' + (s === selectedSize ? ' selected' : '') + '>' + s + '</option>';
      }).join('');
    }
  }

  // ---------- Validation ----------

  function validateDim(raw) {
    if (raw === '' || raw == null) return 'Enter a value.';
    var v = parseFloat(raw);
    if (isNaN(v)) return 'Enter a number.';
    if (v <= 0) return 'Must be greater than zero.';
    if (v < AU.min) return 'Minimum is ' + AU.min + 'm.';
    if (v > AU.max) return 'Maximum is ' + AU.max + 'm.';
    return '';
  }

  function showFieldError(el, msg) {
    if (!el) return;
    if (msg) { el.textContent = msg; el.hidden = false; } else { el.textContent = ''; el.hidden = true; }
  }

  // ---------- Diagram (proportional div grid, ported from the approved design) ----------

  var lastDraw = null;
  function clearDiagram() { diagramGrid.innerHTML = ''; }

  // Re-draw when the diagram's width changes (rotate / resize / layout shift)
  // so the adaptive mark sizes track the real on-screen gaps.
  (function watchDiagramWidth() {
    if (!diagramFrame) return;
    var lastW = 0, timer = null;
    function redraw() {
      var w = diagramFrame.getBoundingClientRect().width;
      if (!lastDraw || !w || Math.abs(w - lastW) < 1) { lastW = w || lastW; return; }
      lastW = w;
      drawDiagram.apply(null, lastDraw);
    }
    function schedule() { clearTimeout(timer); timer = setTimeout(redraw, 120); }
    if (window.ResizeObserver) new ResizeObserver(schedule).observe(diagramFrame);
    else window.addEventListener('resize', schedule);
  })();

  function drawDiagram(joistPositions, supportPositions, joistsAlongLength, total, aspect) {
    clearDiagram();
    // x axis is always the deck length and y the deck width, in both board
    // directions, so length:width is the plotting area's aspect. Clamped
    // (same 0.5-3 range as the US calculator) so extreme decks stay legible.
    if (diagramFrame) diagramFrame.style.setProperty('--tb-ar', String(aspect));
    lastDraw = [joistPositions, supportPositions, joistsAlongLength, total, aspect];
    var dotSize = total > 500 ? 6 : total > 180 ? 9 : total > 70 ? 12 : 16;
    var joistThickness = 4, supportThickness = 4;

    // Dots and lines are fixed-pixel marks on a percentage-positioned grid, so
    // on a narrow screen (or a dense deck) neighbouring marks used to overlap
    // and individual TuffBlocks couldn't be told apart. Cap each mark at a
    // fraction of the real on-screen gap between neighbours. Where there is
    // room (the usual case) the sizes above are untouched.
    function minGapPct(list) {
      var min = Infinity;
      for (var k = 1; k < list.length; k++) { var g = list[k] - list[k - 1]; if (g > 0 && g < min) min = g; }
      return min;
    }
    var gridRect = diagramGrid.getBoundingClientRect();
    if (gridRect.width > 0 && gridRect.height > 0) {
      var jGapPx = minGapPct(joistPositions) / 100 * (joistsAlongLength ? gridRect.height : gridRect.width);
      var sGapPx = minGapPct(supportPositions) / 100 * (joistsAlongLength ? gridRect.width : gridRect.height);
      var minGapPx = Math.min(jGapPx, sGapPx);
      if (isFinite(minGapPx)) dotSize = Math.max(3, Math.min(dotSize, Math.floor(minGapPx * 0.8)));
      if (isFinite(jGapPx)) joistThickness = Math.max(1, Math.min(4, Math.floor(jGapPx * 0.4)));
      if (isFinite(sGapPx)) supportThickness = Math.max(1, Math.min(4, Math.floor(sGapPx * 0.4)));
    }
    var frag = document.createDocumentFragment();

    function lineEl(isHorizontal, pct, thickness, className) {
      var d = document.createElement('div');
      d.className = 'tb-line ' + className;
      if (isHorizontal) {
        d.style.left = '0%'; d.style.top = pct + '%'; d.style.width = '100%'; d.style.height = thickness + 'px'; d.style.marginTop = (-thickness / 2) + 'px';
      } else {
        d.style.top = '0%'; d.style.left = pct + '%'; d.style.height = '100%'; d.style.width = thickness + 'px'; d.style.marginLeft = (-thickness / 2) + 'px';
      }
      return d;
    }

    // Deck-board direction guide lines (11, perpendicular to joists).
    for (var i = 0; i <= 10; i++) {
      frag.appendChild(lineEl(!joistsAlongLength, i * 10, 2, 'tb-line--decking'));
    }
    // Joist lines (perpendicular to board direction).
    joistPositions.forEach(function (p) { frag.appendChild(lineEl(joistsAlongLength, p, joistThickness, 'tb-line--joist')); });
    // Support rows (perpendicular to joists).
    supportPositions.forEach(function (p) { frag.appendChild(lineEl(!joistsAlongLength, p, supportThickness, 'tb-line--support')); });
    // Dots at every joist x support intersection.
    joistPositions.forEach(function (jp) {
      supportPositions.forEach(function (sp) {
        var dot = document.createElement('div');
        dot.className = 'tb-dot';
        dot.style.width = dotSize + 'px'; dot.style.height = dotSize + 'px';
        if (joistsAlongLength) { dot.style.left = sp + '%'; dot.style.top = jp + '%'; }
        else { dot.style.left = jp + '%'; dot.style.top = sp + '%'; }
        frag.appendChild(dot);
      });
    });
    diagramGrid.appendChild(frag);
  }

  // ---------- Main calculation ----------

  function calc() {
    var lenErr = validateDim(lengthInput.value);
    var widErr = validateDim(widthInput.value);
    showFieldError(lengthError, lenErr);
    showFieldError(widthError, widErr);
    var dimsOk = !lenErr && !widErr;

    var lenV = parseFloat(lengthInput.value);
    var widV = parseFloat(widthInput.value);
    var lengthDisplay = dimsOk ? lenV + 'm' : '—';
    var widthDisplay = dimsOk ? widV + 'm' : '—';

    var boardsAlongLength = selectedBoard === 'length';
    var joistsAlongLength = !boardsAlongLength;
    orientationHelp.textContent = 'Joists run perpendicular to the boards: ' + (joistsAlongLength ? ('along deck length (' + lengthDisplay + ')') : ('across deck width (' + widthDisplay + ')')) + '.';

    tierHelp.textContent = selectedTier === 'low'
      ? 'Choose when the underside of your framing is more than 150mm and less than 400mm above ground. Framing at or below 150mm above ground is not covered by this option.'
      : 'Choose when the underside of your framing is 400mm or above ground. Finished height must still be no more than 915mm.';

    var table = currentSpeciesTable();
    var covered = !!table;
    var spanPair = covered ? table[selectedSize] : null;
    var singleSpan = spanPair ? spanPair[0] : 0;
    var continuousSpan = spanPair ? spanPair[1] : 0;

    notCoveredEl.hidden = covered;
    if (!covered) notCoveredEl.textContent = 'This timber combination is not covered by the selected source table.';

    sizeActual.textContent = covered
      ? ('Actual size: ' + selectedSize.replace('x', ' × ') + 'mm. Published joist span: ' + fmtM(singleSpan) + ' single' + (singleSpan !== continuousSpan ? (', ' + fmtM(continuousSpan) + ' continuous') : '') + '. TuffBlock intervals capped at 1.5m.' + (selectedSpecies === 'mgp' ? ' MGP10 spans are taken from the published Hyne T3 Green+ MGP10 deck-joist table and apply to that product only, not to every MGP10 timber.' : ''))
      : '';

    var sizeParts = covered ? selectedSize.split('x').map(Number) : [0, 0];
    var depth = sizeParts[0], width = sizeParts[1];
    var showDeep = covered && depth > width * 4;
    setWarningVisible(warnDeep, showDeep);

    var showNarrow = covered && width === 35;
    setWarningVisible(warnNarrow, showNarrow);

    var showWidth = covered && width === 50;
    setWarningVisible(warnWidth, showWidth);

    var showCypress = covered && selectedSpecies === 'cy' && selectedTier === 'low';
    setWarningVisible(warnCypress, showCypress);

    var showTreatment = covered && selectedSpecies === 'tp' && selectedTier === 'low';
    setWarningVisible(warnTreatment, showTreatment);

    srcText.textContent = selectedTier === 'low'
      ? 'Span references: Timber Queensland TDS13 and the Hyne T3 Green+ MGP10 table (450mm joist centres). MGP10 values apply to the Hyne T3 Green+ product only. Block spacing is capped at 1.5m; overhang is capped at 300mm or the smaller timber limit.'
      : 'Span references: Timber Queensland TDS4 Table 4 and the Hyne T3 Green+ MGP10 table (450mm joist centres). MGP10 values apply to the Hyne T3 Green+ product only. Block spacing is capped at 1.5m; overhang is capped at 300mm or the smaller timber limit.';

    var cantDisabled = !covered;
    overhangDisabledText.hidden = !cantDisabled;
    if (cantDisabled) overhangDisabledText.textContent = 'Choose a covered timber combination to use overhang.';
    overhangToggle.disabled = cantDisabled;
    var overhangOn = !!overhangToggle.checked && !cantDisabled;
    overhangFields.hidden = !overhangOn;

    var overhangMmV = Number(overhangMmInput.value || 0);
    var overhangInputError = '';
    if (overhangOn) {
      if (overhangMmInput.value === '' || overhangMmInput.value == null) overhangInputError = 'Enter an overhang in millimetres.';
      else if (!Number.isInteger(overhangMmV) || overhangMmV <= 0) overhangInputError = 'Overhang must be a whole number greater than zero.';
    }

    var total = null, perRun = '–', joists = '–', packText = '–', actualSpacing = '–', actualSupportSpacing = '–';
    var timber = [];
    var layoutNote = 'Enter valid selections to generate the support layout.';
    var calcError = '';
    var ok = dimsOk && covered && !overhangInputError;
    var combo = null;

    var joistPositionsPct = [], supportPositionsPct = [];

    if (ok) {
      var deckL = lenV * 1000, deckW = widV * 1000;
      var runL = joistsAlongLength ? deckL : deckW;
      var crossW = joistsAlongLength ? deckW : deckL;
      var requestedOverhang = overhangOn ? overhangMmV : 0;

      var r = solve(runL, singleSpan, continuousSpan, requestedOverhang);
      if (!r) {
        calcError = overhangOn
          ? 'This overhang exceeds the calculator’s TuffBlock or timber planning limits. Reduce the overhang or choose another joist size.'
          : 'A support layout could not be generated for these selections.';
        ok = false;
      } else {
        var spaces = Math.max(1, Math.ceil(crossW / AU.spacing - 1e-9));
        var nJ = spaces + 1;
        var actualJoistCentres = crossW / spaces;

        total = r.k * nJ;
        perRun = r.k;
        joists = nJ;
        actualSpacing = Math.round(actualJoistCentres) + 'mm';
        actualSupportSpacing = Math.round(r.s) + 'mm';
        combo = bestPacks(total);
        packText = combo.text;

        joistPositionsPct = Array.from({ length: nJ }, function (_, i) { return i / Math.max(1, nJ - 1) * 100; });
        supportPositionsPct = Array.from({ length: r.k }, function (_, i) { return (r.c + i * r.s) / runL * 100; });

        var runUser = runL / 1000;
        var cutLength = (Math.ceil(runUser / 0.3 - 1e-9) * 0.3).toFixed(1);
        timber = [{ qty: nJ, desc: selectedSize + 'mm joist runs, approx. ' + cutLength + 'm each' }];

        layoutNote = overhangOn
          ? (r.k + (r.k === 1 ? ' support row' : ' support rows') + ' at ' + actualSupportSpacing + ' calculated spacing, with the outer rows ' + Math.round(r.c) + 'mm in from each joist end. Calculated maximum overhang for this layout: ' + r.maxOverhang + 'mm. Each white dot represents one TuffBlock.')
          : (r.k + (r.k === 1 ? ' support row' : ' support rows') + ' at ' + actualSupportSpacing + ' calculated spacing. Outer rows align with the joist ends because no overhang is selected. Each white dot represents one TuffBlock.');
      }
    }

    var overhangHelperText = overhangInputError;
    if (!overhangHelperText && overhangOn && covered && dimsOk) {
      var deckL2 = lenV * 1000, deckW2 = widV * 1000;
      var runL2 = joistsAlongLength ? deckL2 : deckW2;
      var solved = solve(runL2, singleSpan, continuousSpan, overhangMmV);
      if (solved) {
        overhangHelperText = 'Within this calculator’s planning limits. Maximum for this layout: ' + solved.maxOverhang + 'mm (300mm TuffBlock cap, timber span and backspan checked).';
      } else {
        var upper = Math.min(AU.maxOverhang, Math.floor(singleSpan * 0.25), Math.floor((runL2 - 1) / 2));
        var feasible = 0;
        for (var mm = upper; mm >= 1; mm--) { if (solve(runL2, singleSpan, continuousSpan, mm)) { feasible = mm; break; } }
        overhangHelperText = feasible
          ? ('Requested overhang is too large. Maximum for these selections: ' + feasible + 'mm at each end.')
          : 'This calculator cannot establish a positive overhang for these dimensions and timber. Choose no overhang.';
      }
    }
    if (!overhangHelperText) overhangHelperText = 'Overhang is checked against the selected single-span value and the calculated adjacent backspan.';
    overhangHelper.textContent = overhangHelperText;

    // ---------- Results output ----------

    outTotal.textContent = total != null ? total : '–';
    outPerRun.textContent = perRun;
    outJoists.textContent = joists;
    outSpacing.textContent = actualSpacing;
    outSupportSpacing.textContent = actualSupportSpacing;
    outRecommend.textContent = total != null ? ('Suggested pack: ' + packText) : '';

    diagramOkEl.hidden = !ok;
    blockedTextEl.hidden = ok;
    legendEl.hidden = !ok;
    if (!ok) {
      blockedTextEl.textContent = !dimsOk ? 'Enter a valid deck length and width to see the layout.'
        : !covered ? 'This combination is not covered by the source span tables.'
        : (overhangInputError || calcError || 'A support layout could not be generated.');
      clearDiagram();
    } else {
      drawDiagram(joistPositionsPct, supportPositionsPct, joistsAlongLength, total, Math.min(3, Math.max(lenV / widV, 0.5)));
      diagramLengthEl.textContent = 'Length: ' + lengthDisplay;
      diagramWidthEl.textContent = 'Width: ' + widthDisplay;
      layoutNoteEl.textContent = layoutNote;
    }

    var spName = AU.species.filter(function (s) { return s[0] === selectedSpecies; })[0][1];
    var tierName = AU.tiers.filter(function (t) { return t[0] === selectedTier; })[0][1];
    var joistDirectionText = joistsAlongLength ? ('Along deck length (' + lengthDisplay + ')') : ('Across deck width (' + widthDisplay + ')');
    var maxSupportText = ok ? (fmtM(Math.min((perRun === 2 ? singleSpan : continuousSpan), AU.maxBlockSpacing)) + ' (timber span / TuffBlock cap)') : '–';

    var selections = [
      { k: 'Deck', v: dimsOk ? (lengthDisplay + ' × ' + widthDisplay) : '–' },
      { k: 'Deck boards', v: boardsAlongLength ? 'Along deck length' : 'Along deck width' },
      { k: 'Joist direction', v: joistDirectionText },
      { k: 'Timber', v: spName },
      { k: 'Framing clearance', v: tierName },
      { k: 'Joist size', v: covered ? (selectedSize + 'mm') : '–' },
      { k: 'Joist centres', v: ok ? ('450mm maximum; ' + actualSpacing + ' calculated') : '450mm maximum' },
      { k: 'Max TuffBlock spacing', v: maxSupportText },
      { k: 'Overhang', v: overhangOn ? (overhangMmV + 'mm each end') : 'None' }
    ];
    selectionsRows.innerHTML = selections.map(function (s) {
      return '<div class="tb-selections-row"><span class="tb-selections-k">' + s.k + '</span><span class="tb-selections-v">' + s.v + '</span></div>';
    }).join('');

    cutlistRows.innerHTML = timber.map(function (t) {
      return '<div class="tb-cutlist-row"><span>' + t.desc + '</span><span class="tb-qty">' + t.qty + '×</span></div>';
    }).join('');

    lastCombo = (ok && combo) ? { units: combo.units, cost: null } : null;
    updateSticky(total, packText, lastCombo);
  }

  // ---------- Controls ----------

  boardButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      boardButtons.forEach(function (b) { b.classList.remove('active'); });
      btn.classList.add('active');
      selectedBoard = btn.getAttribute('data-board');
      calc();
    });
  });

  tierButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      tierButtons.forEach(function (b) { b.classList.remove('active'); });
      btn.classList.add('active');
      selectedTier = btn.getAttribute('data-tier');
      populateSizes();
      calc();
    });
  });

  function onSpeciesChange(value) {
    selectedSpecies = value;
    populateSpecies();
    populateSizes();
    calc();
  }
  function onSizeChange(value) {
    selectedSize = value;
    populateSizes();
    calc();
  }
  if (speciesSeg) {
    speciesSeg.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-species]');
      if (btn) onSpeciesChange(btn.getAttribute('data-species'));
    });
  }
  if (speciesSelect) {
    speciesSelect.addEventListener('change', function () { onSpeciesChange(speciesSelect.value); });
  }
  if (sizeSeg) {
    sizeSeg.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-size]');
      if (btn) onSizeChange(btn.getAttribute('data-size'));
    });
  }
  if (sizeSelect) {
    sizeSelect.addEventListener('change', function () { onSizeChange(sizeSelect.value); });
  }

  if (seeResultsBtn && resultsPanel) {
    seeResultsBtn.addEventListener('click', function () {
      window.scrollTo({ top: resultsPanel.getBoundingClientRect().top + window.scrollY - 16, behavior: 'smooth' });
    });
  }

  [warnDeep, warnNarrow, warnWidth, warnCypress, warnTreatment].forEach(function (card) {
    if (!card) return;
    var toggleBtn = card.querySelector('.tb-warning__toggle');
    if (!toggleBtn) return;
    toggleBtn.addEventListener('click', function () {
      var open = card.classList.toggle('is-open');
      toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  if (speciesTipBtn && speciesTip) {
    speciesTip.textContent = 'Choose the timber species and stress grade shown on your supplied timber. Different species, grades and sizes have different published joist spans, which changes how many blocks you need.';
    speciesTipBtn.addEventListener('click', function () { speciesTip.hidden = !speciesTip.hidden; });
  }
  if (placeTipBtn && placeTip) {
    placeTipBtn.addEventListener('click', function () { placeTip.hidden = !placeTip.hidden; });
  }

  overhangToggle.addEventListener('change', function () { overhangFields.hidden = !overhangToggle.checked; calc(); });
  overhangMmInput.addEventListener('input', calc);
  overhangMmInput.addEventListener('change', calc);
  [lengthInput, widthInput].forEach(function (el) { el.addEventListener('input', calc); el.addEventListener('change', calc); });

  // Track fill is an inline gradient the slider's own CSS has no color for
  // (background: transparent on both the input and its track pseudo-
  // elements) — matching the approved design, which paints the filled
  // portion this same way rather than via a CSS-only trick.
  function paintSliderFill(slider) {
    var min = parseFloat(slider.min) || 0, max = parseFloat(slider.max) || 100;
    var pct = max > min ? (parseFloat(slider.value) - min) / (max - min) * 100 : 0;
    pct = Math.min(100, Math.max(0, pct));
    slider.style.background = 'linear-gradient(to right, #FD5000 0%, #FD5000 ' + pct + '%, #E1E7EB ' + pct + '%, #E1E7EB 100%)';
  }
  function syncSliderToInput(input, slider) {
    var v = Math.min(12, Math.max(1, parseFloat(input.value) || 1));
    slider.value = v;
    paintSliderFill(slider);
  }
  if (lengthSlider) {
    lengthInput.addEventListener('input', function () { syncSliderToInput(lengthInput, lengthSlider); });
    lengthSlider.addEventListener('input', function () { lengthInput.value = lengthSlider.value; paintSliderFill(lengthSlider); calc(); });
  }
  if (widthSlider) {
    widthInput.addEventListener('input', function () { syncSliderToInput(widthInput, widthSlider); });
    widthSlider.addEventListener('input', function () { widthInput.value = widthSlider.value; paintSliderFill(widthSlider); calc(); });
  }

  if (srcToggle && srcBody) {
    srcToggle.addEventListener('click', function () {
      var open = srcBody.hidden;
      srcBody.hidden = !open;
      srcToggle.setAttribute('aria-expanded', String(open));
    });
  }
  if (disclaimerToggle && disclaimerMore) {
    disclaimerToggle.addEventListener('click', function () {
      var open = disclaimerMore.hidden;
      disclaimerMore.hidden = !open;
      disclaimerToggle.textContent = open ? 'Read less' : 'Read more';
    });
  }
  // Desktop: always visible (no CSS rule restricts #tb-selections-rows above
  // the mobile breakpoint, and the toggle button itself is tb-show-mobile —
  // hidden there — so it's never reachable to re-open if JS force-hid the
  // rows here). Mobile: collapsed by default, toggled open via a class, not
  // the `hidden` property, so nothing here can leave desktop permanently
  // hidden with no way to reveal it again.
  if (selectionsToggle && selectionsRows && selectionsToggleLabel) {
    selectionsToggle.addEventListener('click', function () {
      var open = !selectionsRows.classList.contains('is-open');
      selectionsRows.classList.toggle('is-open', open);
      selectionsToggleLabel.textContent = open ? 'Hide' : 'Show';
    });
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', function () {
      lengthInput.value = AU.defaults.length;
      widthInput.value = AU.defaults.width;
      selectedBoard = AU.defaults.board; selectedSpecies = AU.defaults.species; selectedTier = AU.defaults.tier; selectedSize = AU.defaults.size;
      boardButtons.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-board') === selectedBoard); });
      tierButtons.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-tier') === selectedTier); });
      populateSpecies(); populateSizes();
      overhangToggle.checked = false; overhangFields.hidden = true; overhangMmInput.value = AU.defaults.overhangMm;
      calc();
    });
  }

  // ---------- Intro modal (first-interaction auto-open, matching final US behaviour) ----------

  // The sticky bar's visibility/top depend on whether this modal is open, so both
  // open and close re-evaluate it (function declarations hoist, so the call is
  // safe even though refreshStickyBarVisibility is defined further down).
  function openIntro() { if (!introModal) return; introModal.hidden = false; introModal.classList.add('is-open'); refreshStickyBarVisibility(); }
  function closeIntro() { if (!introModal) return; introModal.classList.remove('is-open'); introModal.hidden = true; introSeen = true; refreshStickyBarVisibility(); }
  introOpenBtns.forEach(function (b) { b.addEventListener('click', openIntro); });
  introCloseBtns.forEach(function (b) { b.addEventListener('click', closeIntro); });
  if (introModal) { introModal.addEventListener('click', function (e) { if (e.target === introModal) closeIntro(); }); }

  var firstInteractionEvents = ['focusin', 'pointerdown', 'input'];
  function onFirstInteraction() {
    if (introSeen) return;
    introSeen = true;
    openIntro();
    firstInteractionEvents.forEach(function (evt) { root.removeEventListener(evt, onFirstInteraction); });
  }
  firstInteractionEvents.forEach(function (evt) { root.addEventListener(evt, onFirstInteraction); });

  // ---------- Add to cart ----------

  function cartCtaReady(combo) { return cartEnabled && !!combo && combo.units > 0 && !!packVariantId; }
  function setCtaText(cta, text) {
    var label = cta.querySelector('[data-tb-cart-cta-label]');
    if (label) label.textContent = text; else cta.textContent = text;
  }
  function updateCartCtas(combo) {
    var ctas = document.querySelectorAll('[data-tb-cart-cta]');
    var ready = cartCtaReady(combo);
    ctas.forEach(function (cta) {
      cta.classList.remove('is-added');
      cta.classList.toggle('is-cart-ready', ready);
      if (ready) { setCtaText(cta, 'Add to cart'); cta.setAttribute('data-tb-cart-ready', 'true'); }
      else { setCtaText(cta, cta.getAttribute('data-shop-label') || 'Shop TuffBlock'); cta.removeAttribute('data-tb-cart-ready'); }
    });
  }
  function addPacksToCart(cta) {
    var combo = lastCombo;
    if (!cartCtaReady(combo)) return;
    var ctas = document.querySelectorAll('[data-tb-cart-cta]');
    ctas.forEach(function (c) { c.classList.add('is-loading'); });
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(packVariantId, 10), quantity: combo.units }] })
    })
      .then(function (res) { if (!res.ok) return res.json().then(function (e) { throw new Error((e && e.description) || 'Could not add to cart'); }); return res.json(); })
      .then(function (cart) {
        ctas.forEach(function (c) { c.classList.remove('is-loading'); c.classList.add('is-added'); setCtaText(c, 'Added to cart'); });
        if (window.jQuery) window.jQuery('body').trigger('updateCart', cart);
        setTimeout(function () { updateCartCtas(lastCombo); }, 2200);
      })
      .catch(function () {
        ctas.forEach(function (c) { c.classList.remove('is-loading'); });
        var url = cta.getAttribute('data-shop-url') || cta.getAttribute('href');
        if (url && url !== '#') window.location.href = url;
      });
  }
  document.querySelectorAll('[data-tb-cart-cta]').forEach(function (cta) {
    if (cta.classList.contains('tb-sticky-cta')) return;
    cta.addEventListener('click', function () {
      if (cta.getAttribute('data-tb-cart-ready') === 'true' && !cta.classList.contains('is-loading')) addPacksToCart(cta);
    });
  });

  // ---------- Desktop sticky strips (top/bottom), gated behind first engagement ----------

  function updateDesktopStrip(total, packText, combo) {
    [desktopStripBottom, desktopStripTop].forEach(function (strip) {
      if (!strip) return;
      var totalEl = strip.querySelector('[data-strip-total]');
      var packEl = strip.querySelector('[data-strip-pack]');
      if (totalEl) totalEl.textContent = total == null ? '–' : total;
      if (packEl) packEl.textContent = total == null ? 'Enter your deck to see a pack suggestion.' : ('Suggested pack: ' + (packText || '—'));
    });
    updateCartCtas(combo);
  }

  function refreshStripTopVisibility() {
    if (!desktopStripTop || !desktopStripSection) return;
    var rect = desktopStripSection.getBoundingClientRect();
    var pastCalc = rect.bottom < 0;
    var hideForModal = !!(introModal && !introModal.hidden);
    desktopStripTop.classList.toggle('is-visible', pastCalc && !hideForModal && !mobileMq.matches && hasEngagedStrip);
  }
  if (desktopStripTop && desktopStripSection && 'IntersectionObserver' in window) {
    new IntersectionObserver(refreshStripTopVisibility, { threshold: 0 }).observe(desktopStripSection);
    window.addEventListener('scroll', refreshStripTopVisibility, { passive: true });
  }

  if (stripRequiresInteraction) {
    [desktopStripBottom, desktopStripTop].forEach(function (strip) { if (strip) strip.style.display = 'none'; });
  }
  var engageEvents = ['focusin', 'pointerdown', 'input'];
  function onEngage() {
    if (hasEngagedStrip) return;
    hasEngagedStrip = true;
    [desktopStripBottom, desktopStripTop].forEach(function (strip) { if (strip) strip.style.display = ''; });
    engageEvents.forEach(function (evt) { root.removeEventListener(evt, onEngage); });
    refreshStripTopVisibility();
    refreshStickyBarVisibility();
  }
  if (stripRequiresInteraction) {
    engageEvents.forEach(function (evt) { root.addEventListener(evt, onEngage); });
  }

  // ---------- Mobile sticky bar: top-anchored, engagement-gated, dual Results/Add-to-cart, dismissible ----------

  function stickyBarTopOffset() {
    var maxBottom = 0;
    var all = document.querySelectorAll('body *');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (!stickyBar || el === stickyBar || stickyBar.contains(el) || el.contains(stickyBar)) continue;
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      // The intro modal and any other full-screen overlay (e.g. a popup scrim) are
      // not header bars: counting one pushed the bar a full screen down, off the
      // viewport, whenever it was positioned while an overlay was open.
      if (introModal && (el === introModal || introModal.contains(el) || el.contains(introModal))) continue;
      var r = el.getBoundingClientRect();
      if (r.height > window.innerHeight * 0.4) continue;
      if (r.top <= 2 && r.height > 0 && r.width > window.innerWidth * 0.5 && r.bottom > maxBottom) maxBottom = r.bottom;
    }
    return maxBottom;
  }

  var stickyForceHide = false;
  function refreshStickyBarVisibility() {
    if (!stickyBar) return;
    var modalOpen = !!(introModal && !introModal.hidden);
    var show = !!mobileMq.matches && lastStickyTotal != null && !stickyForceHide && !modalOpen && hasEngagedStrip && !stickyDismissed;
    stickyBar.hidden = !show;
    if (show) {
      stickyBar.style.top = (stickyBarTopOffset() + 16) + 'px';
      if (stickyTotal) stickyTotal.textContent = lastStickyTotal;
      if (stickyPack) stickyPack.textContent = lastStickyPackText || '';
    }
  }

  function updateSticky(total, packText, combo) {
    lastStickyTotal = total;
    lastStickyPackText = packText;
    updateDesktopStrip(total, packText, combo);
    refreshStickyBarVisibility();
  }

  if (stickyBar && resultsPanel && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      var visible = entries[0].isIntersecting;
      stickyForceHide = visible;
      refreshStickyBarVisibility();
    }, { threshold: 0 }).observe(resultsPanel);
  }

  if (stickyBar) {
    var stickyDismissBtn = stickyBar.querySelector('[data-tb-sticky-dismiss]');
    if (stickyDismissBtn) {
      stickyDismissBtn.addEventListener('click', function () {
        stickyDismissed = true;
        try { sessionStorage.setItem('tb-au-sticky-dismissed', '1'); } catch (err) {}
        refreshStickyBarVisibility();
      });
    }
    stickyBar.addEventListener('click', function (e) {
      if (e.target && e.target.closest('[data-tb-sticky-dismiss]')) return;
      var cta = stickyBar.querySelector('.tb-sticky-cta');
      if (cta && cta.getAttribute('data-tb-cart-ready') === 'true') {
        if (!cta.classList.contains('is-loading')) addPacksToCart(cta);
        return;
      }
      window.scrollTo({ top: resultsPanel.getBoundingClientRect().top + window.scrollY - 16, behavior: 'smooth' });
    });
  }

  // ---------- Load a preset (hero tiles / quick-reference table / worked example) ----------
  // Narrow public API hung off #tb-calc, same shape as the US calculator's
  // loadPreset(), for the AU standalone page's own hero tiles/table/stepper.
  function loadPreset(preset) {
    if (!preset) return;
    if (preset.lengthM != null) lengthInput.value = preset.lengthM;
    if (preset.widthM != null) widthInput.value = preset.widthM;
    if (lengthSlider) syncSliderToInput(lengthInput, lengthSlider);
    if (widthSlider) syncSliderToInput(widthInput, widthSlider);
    if (preset.board) { selectedBoard = preset.board; boardButtons.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-board') === selectedBoard); }); }
    if (preset.tier) { selectedTier = preset.tier; tierButtons.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-tier') === selectedTier); }); }
    if (preset.species) { selectedSpecies = preset.species; populateSpecies(); }
    populateSizes();
    if (preset.size) { selectedSize = preset.size; populateSizes(); }
    overhangToggle.checked = !!preset.overhang;
    overhangFields.hidden = !overhangToggle.checked;
    if (preset.overhangMm != null) overhangMmInput.value = preset.overhangMm;
    calc();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  root.loadPreset = loadPreset;

  // ---------- Init ----------

  populateSpecies();
  populateSizes();
  if (lengthSlider) syncSliderToInput(lengthInput, lengthSlider);
  if (widthSlider) syncSliderToInput(widthInput, widthSlider);
  calc();
})();
